import { SignatureFrame } from "./SignatureFrame";
import { CENTERS, INSTITUTE_RESEARCH } from "../../lib/researchCenters";

/**
 * The centre web. Nodes are the real centres from researchCenters.ts — one per
 * published profile, sized by active scholar count — and every numeral comes
 * from INSTITUTE_RESEARCH. Nothing here is hardcoded, so the graphic stays true
 * as the remaining centres return their blocks.
 *
 * `hero` renders the compact form used in the /research hero aside: just the
 * hub and its nodes, in a near-square viewBox. The wide form's two numeral
 * columns are dropped there — squeezed into a ~536px column they scaled to
 * roughly half size and went unreadable, and the hero's own lead sentence
 * already states all four figures.
 */

const WIDE = { cx: 460, cy: 200, box: "0 0 920 400", rings: [60, 100, 140, 180], nodeR: 128, labelR: 152 };
const HERO = { cx: 232, cy: 196, box: "56 34 364 324", rings: [52, 84, 116, 148], nodeR: 106, labelR: 128 };

const R = INSTITUTE_RESEARCH;
const MAX_SCHOLARS = Math.max(...CENTERS.map((c) => c.scholars ?? 0), 1);

function buildNodes(g: typeof WIDE) {
  return CENTERS.map((c, i) => {
    // Start at 12 o'clock and step evenly around the circle.
    const angle = -90 + (360 / CENTERS.length) * i;
    const rad = (angle * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const scholars = c.scholars ?? 0;
    return {
      slug: c.slug,
      label: c.short,
      x: g.cx + cos * g.nodeR,
      y: g.cy + sin * g.nodeR,
      labelX: g.cx + cos * g.labelR,
      labelY: g.cy + sin * g.labelR,
      // Radiate labels outward so they clear the ring instead of stacking on it.
      anchor: cos > 0.3 ? "start" : cos < -0.3 ? "end" : "middle",
      dy: sin > 0.3 ? 12 : sin < -0.3 ? -6 : 4,
      r: 6 + (scholars / MAX_SCHOLARS) * 9,
      big: scholars >= MAX_SCHOLARS / 2,
    } as const;
  });
}

export function ResearchWeb({ hero = false }: { hero?: boolean } = {}) {
  const g = hero ? HERO : WIDE;
  const CX = g.cx;
  const CY = g.cy;
  const RINGS = g.rings;
  const NODES = buildNodes(g);
  return (
    <SignatureFrame
      className={hero ? "is-hero" : ""}
      title={`${R.centres} VTU-recognised research centres, networked`}
      code="NET-RES-004"
      meta={`${R.scholars} active scholars · ₹${R.grantsCr.toFixed(2)} cr in grants`}
    >
      <svg viewBox={g.box} preserveAspectRatio="xMidYMid meet">
        {RINGS.map((r, i) => (
          <circle
            key={i}
            cx={CX}
            cy={CY}
            r={r}
            className="illo-stroke"
            strokeWidth="0.5"
            strokeDasharray="2 4"
            opacity="0.4"
            fill="none"
          />
        ))}

        {NODES.map((n) => (
          <line
            key={n.slug}
            x1={CX}
            y1={CY}
            x2={n.x}
            y2={n.y}
            className="illo-stroke-bold illo-draw-2"
          />
        ))}

        {NODES.map((n, i) => (
          <g key={n.slug} className={`illo-pop illo-pop-d${Math.min(Math.floor(i / 3) + 1, 5)}`}>
            <circle
              cx={n.x}
              cy={n.y}
              r={n.r}
              className={n.big ? "illo-gold-fill" : "illo-fill"}
              stroke={n.big ? "var(--gold-700)" : "var(--navy-900)"}
              strokeWidth="1.5"
            />
            <text
              x={n.labelX}
              y={n.labelY + n.dy}
              textAnchor={n.anchor}
              className="illo-label-bold"
            >
              {n.label}
            </text>
          </g>
        ))}

        <g className="illo-pop">
          <circle
            cx={CX}
            cy={CY}
            r="42"
            className="illo-fill-paper illo-stroke-bold"
            strokeWidth="2"
          />
          <circle
            cx={CX}
            cy={CY}
            r="32"
            className="illo-gold-stroke"
            strokeWidth="1.5"
            strokeDasharray="3 3"
          />
          <text x={CX} y={CY - 4} textAnchor="middle" className="illo-numeral" style={{ fontSize: "20px" }}>
            {R.centres}
          </text>
          <text x={CX} y={CY + 14} textAnchor="middle" className="illo-label">
            CENTRES
          </text>
        </g>

        {!hero && <g className="illo-pop illo-pop-d4">
          <text x="40" y="60" className="illo-label-bold">
            RESEARCH GRANTS
          </text>
          <line x1="40" y1="70" x2="160" y2="70" className="illo-gold-stroke" strokeWidth="2" />
          <text
            x="40"
            y="115"
            className="illo-numeral illo-numeral-gold"
            style={{ fontSize: "36px" }}
          >
            ₹{R.grantsCr.toFixed(2)}
          </text>
          <text x="40" y="135" className="illo-label">
            CRORE
          </text>

          <text x="40" y="180" className="illo-label-bold">
            PUBLICATIONS · LAST 5 YEARS
          </text>
          <line x1="40" y1="190" x2="160" y2="190" className="illo-stroke" strokeWidth="1.5" />
          <text x="40" y="225" className="illo-numeral" style={{ fontSize: "30px" }}>
            {R.publications}
          </text>
          <text x="40" y="243" className="illo-label">
            INDEXED
          </text>
        </g>}

        {!hero && <g className="illo-pop illo-pop-d5">
          <text x="880" y="60" textAnchor="end" className="illo-label-bold">
            SCHOLARS
          </text>
          <line x1="800" y1="70" x2="880" y2="70" className="illo-gold-stroke" strokeWidth="2" />
          <text
            x="880"
            y="115"
            textAnchor="end"
            className="illo-numeral illo-numeral-gold"
            style={{ fontSize: "36px" }}
          >
            {R.scholars}
          </text>
          <text x="880" y="135" textAnchor="end" className="illo-label">
            PHD · ACTIVE
          </text>

          <text x="880" y="180" textAnchor="end" className="illo-label-bold">
            PROFILES PUBLISHED
          </text>
          <line x1="800" y1="190" x2="880" y2="190" className="illo-stroke" strokeWidth="1.5" />
          <text
            x="880"
            y="225"
            textAnchor="end"
            className="illo-numeral"
            style={{ fontSize: "30px" }}
          >
            {CENTERS.length}
          </text>
          <text x="880" y="243" textAnchor="end" className="illo-label">
            OF {R.centres} CENTRES
          </text>
        </g>}
      </svg>
    </SignatureFrame>
  );
}
